import { CloseButton, Modal } from "@mantine/core";
import * as React from "react";
import { getDeviceOptions } from "../state/selectors";
import type { DeviceOption, ScannedSerialRow } from "../types";
import { normalizeSerial } from "../utils/serial";
import { DIALOG_MODAL_PROPS, DialogButton, DialogHeader } from "./dialogs";

interface DevicePickerModalProps {
  opened: boolean;
  /** Serial thẻ BHMR đang chọn thiết bị */
  cardSerial: string | null;
  rows: ScannedSerialRow[];
  onClose: () => void;
  onConfirm: (cardSerial: string, deviceSerial: string) => void;
  /** Gỡ thiết bị đang gắn khỏi thẻ */
  onUnlink: (cardSerial: string) => void;
}

const serialKey = (serial: string | null | undefined) => (serial ?? "").trim();

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const ShieldPlusIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1F5FAE"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
    <path d="M12 9v6M9 12h6" />
  </svg>
);

const BarcodeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5C6570" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 6v12M7 6v12M11 6v12M14 6v12M18 6v12M20 6v12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5C6570" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const EmptyNote = ({ children }: { children: React.ReactNode }) => (
  <p
    style={{
      margin: 0,
      padding: "18px 14px",
      border: "1px dashed #DDE0DA",
      borderRadius: 12,
      background: "#FAFAF8",
      color: "#5C6570",
      textAlign: "center",
    }}
  >
    {children}
  </p>
);

interface DeviceOptionListProps {
  options: DeviceOption[];
  selectedSerial: string | null;
  readOnlyLink: boolean;
  radioName: string;
  lineNumbers: Map<string, number>;
  onSelect: (serial: string) => void;
}

/** Danh sách thiết bị. Memo: gõ / quét vào ô lọc không vẽ lại danh sách cho tới khi kết quả lọc đổi */
const DeviceOptionListBase = ({ options, selectedSerial, readOnlyLink, radioName, lineNumbers, onSelect }: DeviceOptionListProps) => (
  <>
    {options.map(({ row, disabled, reason }) => {
      const serial = serialKey(row.serial);
      const checked = !disabled && serial === selectedSerial;
      const line = lineNumbers.get(serial);
      return (
        <label
          key={serial}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            // Viền 2px khi đang chọn, bớt padding để dòng không nhảy chiều cao
            padding: checked ? 13 : 14,
            border: checked ? "2px solid #1F5FAE" : `1px solid ${disabled ? "#E4E6E1" : "#D5D9D2"}`,
            background: checked ? "#F2F7FD" : disabled ? "#FAFAF8" : "#FFFFFF",
            color: disabled ? "#6B737C" : undefined,
            borderRadius: 12,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <input
            type="radio"
            name={radioName}
            value={serial}
            checked={checked}
            disabled={disabled || readOnlyLink}
            onChange={() => onSelect(serial)}
            style={{ width: 18, height: 18, margin: 0, flexShrink: 0, accentColor: "#1F5FAE" }}
          />
          <span style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{serial}</span>
            <span style={{ color: disabled ? undefined : "#5C6570", fontSize: 13 }}>
              {[row.itemName || row.itemCode, line ? `dòng ${line}` : null].filter(Boolean).join(" · ")}
            </span>
          </span>
          {disabled ? (
            <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, textAlign: "right" }}>{reason}</span>
          ) : (
            <span
              style={{
                flexShrink: 0,
                padding: "4px 10px",
                borderRadius: 999,
                background: "#E4F2EA",
                color: "#14532F",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {reason}
            </span>
          )}
        </label>
      );
    })}
  </>
);
const DeviceOptionList = React.memo(DeviceOptionListBase);

/**
 * Hộp chọn thiết bị cho thẻ bảo hành mở rộng (BHMR). Chỉ liệt kê thiết bị trên phiếu này;
 * thiết bị không gắn được vẫn hiện, bị khoá kèm lý do. Quét / gõ serial để lọc, Enter chọn khi chỉ còn một lựa chọn.
 */
const DevicePickerModal = ({ opened, cardSerial, rows, onClose, onConfirm, onUnlink }: DevicePickerModalProps) => {
  const radioName = React.useId();
  const inputId = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<string | null>(null);
  const [focused, setFocused] = React.useState(false);

  // Giữ thẻ đang mở để nội dung không trống trong lúc hộp đóng dần
  const [shownCard, setShownCard] = React.useState<string | null>(null);
  const openKey = opened ? serialKey(cardSerial) || null : null;
  const [lastOpenKey, setLastOpenKey] = React.useState<string | null>(null);

  const cardKey = openKey ?? shownCard ?? "";
  // Hộp đóng hẳn thì cardKey rỗng (shownCard xoá ở onExitTransitionEnd): không tính gì theo rows mỗi lần quét
  const card = React.useMemo(
    () => (cardKey ? rows.find((row) => serialKey(row.serial) === cardKey) : undefined),
    [rows, cardKey],
  );
  const options = React.useMemo(() => (cardKey ? getDeviceOptions(rows, cardKey) : []), [rows, cardKey]);

  // Mỗi lần mở (hoặc đổi thẻ): xoá ô lọc, chọn sẵn thiết bị đang gắn với thẻ
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (openKey) {
      setShownCard(openKey);
      setQuery("");
      const current = rows.find((row) => serialKey(row.serial) === openKey);
      setSelected(serialKey(current?.linkedDeviceSerial) || null);
    }
  }

  const lineNumbers = React.useMemo(
    () =>
      cardKey
        ? new Map(rows.map((row, index): [string, number] => [serialKey(row.serial), index + 1]))
        : new Map<string, number>(),
    [rows, cardKey],
  );

  const subtitle = React.useMemo(() => {
    if (!card) return null;
    const targets = (card.warrantyTargetItemCodes ?? []).map((code) => serialKey(code)).filter(Boolean);
    // Mã áp dụng hiện theo tên hàng trên phiếu nếu có, không thì hiện mã
    const targetNames = Array.from(
      new Set(
        targets.map(
          (code) => rows.find((row) => !row.isWarrantyCard && serialKey(row.itemCode) === code)?.itemName || code,
        ),
      ),
    );
    return [card.itemName, targetNames.length ? `áp dụng cho ${targetNames.join(", ")}` : null]
      .filter(Boolean)
      .join(" · ");
  }, [card, rows]);

  // Chuẩn hoá serial / mã một lần cho mỗi danh sách, không phải mỗi ký tự gõ
  const searchable = React.useMemo(
    () =>
      options.map((option) => ({
        option,
        serial: normalizeSerial(option.row.serial),
        code: normalizeSerial(option.row.itemCode),
      })),
    [options],
  );
  const filterOptions = React.useCallback(
    (key: string) =>
      key ? searchable.filter((item) => item.serial.includes(key) || item.code.includes(key)).map((item) => item.option) : options,
    [searchable, options],
  );
  const normalizedQuery = normalizeSerial(query);
  // Ô nhập nhận ký tự ngay; danh sách lọc theo giá trị hoãn, React bỏ qua các lượt vẽ danh sách đã cũ khi máy quét gõ nhanh
  const deferredQuery = normalizeSerial(React.useDeferredValue(query));
  const visible = React.useMemo(() => filterOptions(deferredQuery), [filterOptions, deferredQuery]);

  const readOnlyLink = !!card?.linkReadOnly;
  const selectedOption = options.find((option) => !option.disabled && serialKey(option.row.serial) === selected);
  const selectedSerial = selectedOption ? serialKey(selectedOption.row.serial) : null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    // Lọc theo ô nhập hiện tại, không theo danh sách đang hoãn: Enter ngay sau khi quét vẫn chọn đúng
    const selectable = filterOptions(normalizedQuery).filter((option) => !option.disabled);
    // Quét đúng serial thì chọn luôn; gõ một phần thì chỉ chọn khi còn đúng một lựa chọn
    const pick =
      selectable.find((option) => normalizeSerial(option.row.serial) === normalizedQuery) ??
      (selectable.length === 1 ? selectable[0] : undefined);
    if (pick) setSelected(serialKey(pick.row.serial));
  };

  const confirm = () => {
    if (cardKey && selectedSerial && !readOnlyLink) onConfirm(cardKey, selectedSerial);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      {...DIALOG_MODAL_PROPS}
      size={680}
      yOffset="14dvh"
      // Hộp lỗi chặn vừa đóng sẽ trả focus về nút Hoàn thành; mở xong thì đưa focus về ô quét
      onEnterTransitionEnd={() => inputRef.current?.focus()}
      // Đóng hẳn rồi mới xoá thẻ đang hiện, để hiệu ứng đóng dần vẫn còn nội dung
      onExitTransitionEnd={() => setShownCard(null)}
    >
      <DialogHeader
        icon={<ShieldPlusIcon />}
        iconBackground="#E6EEF8"
        title={
          <>
            Gắn thiết bị cho thẻ {cardKey}
          </>
        }
        description={subtitle}
        closeButton={
          <CloseButton size={36} radius={8} aria-label="Đóng" icon={<CloseIcon />} onClick={onClose} />
        }
      />

      <div style={{ padding: "16px 24px 0" }}>
        <label
          htmlFor={inputId}
          style={{
            height: 44,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "0 12px",
            border: `1px solid ${focused ? "#1F5FAE" : "#D5D9D2"}`,
            boxShadow: focused ? "0 0 0 3px #E3ECF7" : undefined,
            borderRadius: 10,
            cursor: "text",
          }}
        >
          <BarcodeIcon />
          <span style={VISUALLY_HIDDEN}>Quét hoặc gõ serial thiết bị trên phiếu</span>
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            data-autofocus
            autoComplete="off"
            spellCheck={false}
            placeholder="Quét hoặc gõ serial"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              flexGrow: 1,
              minWidth: 0,
              border: 0,
              outline: "none",
              padding: 0,
              fontFamily: "inherit",
              fontSize: 14,
              textTransform: "uppercase",
              background: "transparent",
              color: "#1B1F24",
            }}
          />
        </label>
      </div>

      <fieldset style={{ border: 0, margin: 0, padding: "12px 24px 0", minWidth: 0 }}>
        <legend style={VISUALLY_HIDDEN}>Thiết bị trên phiếu</legend>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            maxHeight: "max(160px, calc(72dvh - 300px))",
            overflowY: "auto",
          }}
        >
          {options.length === 0 ? (
            <EmptyNote>Chưa có thiết bị nào trên phiếu để gắn.</EmptyNote>
          ) : visible.length === 0 ? (
            <EmptyNote>
              Không có thiết bị nào khớp {deferredQuery}.
            </EmptyNote>
          ) : (
            <DeviceOptionList
              options={visible}
              selectedSerial={selectedSerial}
              readOnlyLink={readOnlyLink}
              radioName={radioName}
              lineNumbers={lineNumbers}
              onSelect={setSelected}
            />
          )}
        </div>
      </fieldset>

      {readOnlyLink && (
        <p style={{ margin: "14px 24px 0", color: "#8A4B05", fontSize: 13 }}>
          Thẻ đã gắn thiết bị từ đơn bán, không đổi được.
        </p>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "20px 24px 24px" }}>
        {cardKey && card?.linkedDeviceSerial && !readOnlyLink && (
          <span style={{ marginRight: "auto" }}>
            <DialogButton tone="secondary" onClick={() => onUnlink(cardKey)}>
              Gỡ thiết bị
            </DialogButton>
          </span>
        )}
        <DialogButton tone="secondary" onClick={onClose}>
          Huỷ
        </DialogButton>
        <DialogButton disabled={!selectedSerial || readOnlyLink} onClick={confirm}>
          {selectedSerial ? `Gắn ${selectedSerial}` : "Gắn thiết bị"}
        </DialogButton>
      </div>
    </Modal>
  );
};

export default React.memo(DevicePickerModal);
